import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip as chartTooltip } from 'chart.js';
import { TextField, Switch, Select, MenuItem, FormControl, InputLabel, Grid, Paper, Typography, Box, Tooltip } from '@mui/material'; // Import Grid and Paper
import ChartDataLabels from 'chartjs-plugin-datalabels'; 
import loadingGif from '../assets/loading.gif';
import loadingGif5 from '../assets/loading4.gif';
import Swal from 'sweetalert2';
import { Line } from 'react-chartjs-2';
import { ToastContainer, toast } from 'react-toastify';
import API_BASE_URL from '../config';
import './Dashboard.css';

import { useWebSocket } from './ws/WebsocketContext';

ChartJS.register(BarElement, CategoryScale, LinearScale, chartTooltip, ChartDataLabels);


const Dashboard = () => {
  const token = localStorage.getItem('token');
  const socket = useWebSocket();
  const [organization, setOrganization] = useState(null);
  const [jobFlowName, setJobFlowName] = useState('New Job Flow');
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([
      { id: 1, stepName: 'Step 1', stepNumber: -1, stepColor: '#d9d9d9', isAccessible: false, jobCount: 10 },
      { id: 2, stepName: 'Step 2', stepNumber: -1, stepColor: '#d1c842', isAccessible: false, jobCount: 10 },
      { id: 3, stepName: 'Step 3', stepNumber: -1, stepColor: '#683b08', isAccessible: false, jobCount: 10 },
      { id: 4, stepName: 'Completed', stepNumber: -1, stepColor: '#2b2b2b', isAccessible: false, jobCount: 10 },
  ]);
  const navigate = useNavigate();
  const [jobFlows, setJobFlows] = useState('');
  const [selectedJobFlow, setSelectedJobFlow] = useState('');
  const [currentJobRead, setCurrentOrderRead] = useState(0);

  const [orgId, setOrgId] = useState(null);

  const [recentOrders, setRecentOrders] = useState([]);
  const [recentUrgentOrders, setRecentUrgentOrders] = useState([]);

  const [recentOrderLoading, setRecentOrderLoading] = useState(false);
  const [urgentOrderLoading, setUrgentOrderLoading] = useState(false);

  const [overallStatus, setOverallStatus] = useState(null);
  const [overallStatusLoading, setOverallStatusLoading] = useState(false);
  const [productManagementRead, setProductManagementRead] = useState(0);
  const [showPrice, setShowPrice] = useState(false);
  const [currentOrderPrices, setCurrentOrderPrices] = useState([]);
  const [fullTableData, setFullTableData] = useState([]);

  useEffect(() => {
    document.title = "Dashboard - IMS";
    
    return () => {
      document.title = "IMS";
    };
  }, []);

  useEffect(() => {
    if (!token || !orgId) return;

    const fetchRecentOrders = async () => {
      setRecentOrderLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/orders/recent-orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          }
        });

        if (response?.data?.length > 0) {
          setRecentOrders(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch recent orders:', error);
      } finally {
        setRecentOrderLoading(false);
      }
    };
    fetchRecentOrders();

  }, [token, orgId]);

  useEffect(() => {
    if (!token || !orgId || !selectedJobFlow) return;

    const fetchRecentUrgentOrders = async () => {
      setUrgentOrderLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/orders/recent-urgent-orders`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
          params: {
            jobFlowId: selectedJobFlow,
          },
        });

        if (response?.data?.length > 0) {
          setRecentUrgentOrders(response.data);
        } else {
          setRecentUrgentOrders([]);
        }
      } catch (error) {
        console.error('Failed to fetch recent urgent orders:', error);
      } finally {
        setUrgentOrderLoading(false);
      }
    };

    fetchRecentUrgentOrders();
  }, [token, orgId, selectedJobFlow]);

  useEffect(() => {
    if (!token || !orgId || !selectedJobFlow) return;

    const fetchOverallStatus = async () => {
      setOverallStatusLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/orders/overall-status`, {
          headers: {
            Authorization: `Bearer ${token}`,
            selectedorg: orgId,
          },
          params: {
            jobFlowId: selectedJobFlow,
          },
        });

        if (response?.data) {
          setOverallStatus(response.data);
        } else {
          setOverallStatus(null);
        }
      } catch (error) {
        console.error('Failed to fetch overall status:', error);
        setOverallStatus(null);
      } finally {
        setOverallStatusLoading(false);
      }
    };

    fetchOverallStatus();
  }, [token, orgId, selectedJobFlow]);

  const closeLoadingAlert = () => Swal.close();

  const fetchJobFlowSteps = async () => {
    if (!selectedJobFlow || !orgId) return;

    try {
        const response = await axios.get(`${API_BASE_URL}/orders/steps-access`, {
            headers: {
                Authorization: `Bearer ${token}`,
                selectedorg: orgId,
            },
            params: { selectedJobFlow },
        });

        if (response?.data?.length > 0) {
          // jobcount to be number from the job step before, and output should not be including first step
          const stepsData = response.data.map((step, index) => ({
            ...step,
            jobCount: index === 0 ? 0 : response.data[index - 1].jobCount,
          }));
          setFullTableData(stepsData);
          // exclude first step
          setTableData(stepsData.slice(1));
        } else {
          setTableData([]);
        }
    } catch (error) {
        console.error('Error fetching job flow steps:', error);
        navigate('/unauthorized');
    }
  };

  useEffect(() => {
    if (socket && selectedJobFlow) {
      const subscribe = () => {
        try {
          socket.send(JSON.stringify({
            type: 'subscribe',
            channel: `jobflow:${selectedJobFlow}`
          }));
          // console.log(`Subscribed to jobflow:${selectedJobFlow}`);
        } catch (error) {
          console.error('WebSocket subscription error:', error);
        }
      };

      subscribe();
      
      return () => {
        try {
          socket.send(JSON.stringify({
            type: 'unsubscribe',
            channel: `jobflow:${selectedJobFlow}`
          }));
          console.log(`Unsubscribed from jobflow:${selectedJobFlow}`);
        } catch (error) {
          console.error('WebSocket unsubscription error:', error);
        }
      };
    }
  }, [socket, selectedJobFlow]);

  useEffect(() => {
    if (socket && orgId) {
      const subscribeDashboard = () => {
        try {
          socket.send(JSON.stringify({
            type: 'subscribe',
            channel: `dashboard:${orgId}`
          }));
          // console.log(`Subscribed to dashboard:${orgId}`);
        } catch (error) {
          console.error('WebSocket dashboard subscription error:', error);
        }
      };

      subscribeDashboard();
      
      return () => {
        try {
          socket.send(JSON.stringify({
            type: 'unsubscribe',
            channel: `dashboard:${orgId}`
          }));
          console.log(`Unsubscribed from dashboard:${orgId}`);
        } catch (error) {
          console.error('WebSocket dashboard unsubscription error:', error);
        }
      };
    }
  }, [socket, orgId]);

  useEffect(() => {
    if (socket) {
        const handleMessage = (event) => {
        try {
            const message = JSON.parse(event.data);
            console.log('Received WebSocket message:', message);

            if (message.channel === `jobflow:${selectedJobFlow}`) {
                fetchJobFlowSteps();
                toast.info(message.data || 'Default message');
            }
            } catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };

        socket.addEventListener('message', handleMessage);
        return () => socket.removeEventListener('message', handleMessage);
    }
  }, [socket, selectedJobFlow]);

// Fetch orgId on mount and wait for it
useEffect(() => {
    const orgIdFromStorage = localStorage.getItem('selectedOrg');
    if (orgIdFromStorage) {
        setOrgId(orgIdFromStorage);
        closeLoadingAlert();
    } else {
        // Poll for orgId until it's available
        const interval = setInterval(() => {
            const storedOrgId = localStorage.getItem('selectedOrg');
            if (storedOrgId) {
                setOrgId(storedOrgId);
                closeLoadingAlert();
                clearInterval(interval);
            }
        }, 500); // Check every 500ms
    }
}, []);

// Fetch data after orgId is ready
useEffect(() => {
    const fetchDashboardData = async () => {
        if (!orgId || !token) return;

        try {
            const dashboardResponse = await axios.get(`${API_BASE_URL}/orders/dashboard-permissions`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    selectedorg: orgId,
                },
            });

            if (dashboardResponse?.data?.[0]?.dashboard === 1) {
                setCurrentOrderRead(dashboardResponse.data[0].current_order_read || 0);
                setProductManagementRead(dashboardResponse.data[0].product_management_read || 0);
            }

            const jobFlowResponse = await axios.get(`${API_BASE_URL}/orders/job-flow`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    selectedorg: orgId,
                },
            });

            if (jobFlowResponse?.data?.length > 0) {
                setJobFlows(jobFlowResponse.data);
                setSelectedJobFlow(jobFlowResponse.data.find((flow) => flow.default_job_flow === 1)?.id || '');
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
            navigate('/unauthorized');
        } finally {
            setLoading(false);
        }
    };

    fetchDashboardData();
  }, [orgId, token, navigate]);

  // Fetch steps for selected job flow
  useEffect(() => {
      fetchJobFlowSteps();
  }, [selectedJobFlow, orgId, token, navigate]);

  useEffect(() => {
      if (showPrice && selectedJobFlow && orgId) {
          fetchCurrentOrderPrices();
      } else {
          setCurrentOrderPrices([]);
      }
  }, [showPrice, selectedJobFlow, orgId, token, navigate]);

  const fetchCurrentOrderPrices = async () => {
      if (!selectedJobFlow) return;
      try {
          const response = await axios.get(`${API_BASE_URL}/orders/current-order-prices`, {
            headers: {
              Authorization: `Bearer ${token}`,
              selectedorg: orgId,
              selectedjobflow: selectedJobFlow,
            },
          });

          if (response?.data?.length > 0) {
            setCurrentOrderPrices(response.data);
          }
      } catch (error) {
          console.error('Error fetching current order prices:', error);
      }
  };

  const handleBarClick = (event) => {
    if (currentJobRead === 1) {
        if (event?.[0]?.index !== undefined) {
            const stepNumber = event[0].index + 1;

            const stepData = fullTableData.find((item) => item.stepNumber === stepNumber);

            if (stepData && stepData.isAccessible) {
                navigate(`/order/current-order`, {
                    state: { redirectJobFlowId: selectedJobFlow, redirectStepId: stepData.id },
                });
            } else {
                console.log('Step is not accessible or not found.');
            }
        }
    }
  };
  
  const chartData = { 
    labels: tableData.map(row => row.stepName),
    datasets: [
      {
        label: 'Job Flow Steps',
        data: tableData.map(row => row.jobCount || 0),
        backgroundColor: tableData.map(row => row.stepColor || '#3e5570'),
        borderRadius: 10,
        hoverBorderWidth: 4,
        hoverBorderColor: '#ffffff',
      },
    ],
  }

  const chartOptions = React.useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        display: false,
        grid: {
          display: false,
          drawBorder: false,
        },
        min: 0,
      },
      x: {
        grid: { display: false },
        ticks: { 
          color: tableData.map(row => row.isAccessible ? '#3e5570' : '#cccccc'), 
          font: { size: 14, family: 'Roboto, Arial' }, 
          maxRotation: 45, 
          minRotation: 0 
        },
        border: { display: false },
      },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: '#3e5570',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#d1c842',
        borderWidth: 2,
        callbacks: {
          label: (tooltipItem) => {
            const stepIndex = tooltipItem.dataIndex;
            const stepName = tooltipItem.label;
            const jobCount = tooltipItem.parsed.y;
            
            const stepData = tableData[stepIndex];
            if (!stepData.isAccessible) {
              return 'No Access'; 
            }
            
            // Check if price data should be shown
            if (showPrice && currentOrderPrices.length > 0) {
              
              // Find price data for this step (step_number is 1-based)
              const priceData = currentOrderPrices.find(p => p.step_number === stepIndex + 1);
              
              if (priceData) {
                const priceText = priceData.price === -1 ? 'Unauthorized' : `$${priceData.price.toLocaleString()}`;
                return [`${jobCount} jobs | ${priceText}`];
              } else {
                return `${jobCount} jobs`;
              }
            } else {
              return `${jobCount} jobs`;
            }
          },
        },
      },
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'end',
        color: tableData.map((row, index) => row.isAccessible ? '#3e5570' : 'transparent'), // 修改这里：不可访问时透明
        font: { size: 16, weight: 'bold' },
        formatter: (value, context) => {
          const stepData = tableData[context.dataIndex];
          return stepData.isAccessible ? value : '';
        },
      },
    },
    animation: {
      duration: 1000,
      easing: 'easeOutCubic',
    },
    bar: {
      borderRadius: 10,
      maxBarThickness: 36,
    },
    layout: {
      padding: { top: 36, left: 12, right: 12, bottom: 12 },
    },
    onClick: (event, elements) => handleBarClick(elements),
  }), [showPrice, currentOrderPrices, tableData]);

  function AnimatedNumber({ value, color, small }) {
    const [displayValue, setDisplayValue] = React.useState(value);
    React.useEffect(() => {
      let start = displayValue;
      let end = value;
      if (start === end) return;
      let frame = 0;
      const totalFrames = 16;
      const step = () => {
        frame++;
        const progress = frame / totalFrames;
        setDisplayValue(Math.round(start + (end - start) * progress));
        if (frame < totalFrames) requestAnimationFrame(step);
        else setDisplayValue(end);
      };
      step();
      // eslint-disable-next-line
    }, [value]);
    return (
      <div style={{
        fontSize: small ? 22 : 36,
        fontWeight: 700,
        color: color,
        marginTop: 2,
        transition: 'color 0.2s',
        fontVariantNumeric: 'tabular-nums',
        letterSpacing: 1,
      }}>{displayValue}</div>
    );
  }
    
  return (
  <div style={{ height: 'calc(100vh - 65px)', display: 'flex', flexDirection: 'column', padding: '10px', backgroundColor: '#ffffffff' }}>
    <ToastContainer position="top-right" autoClose={10000} limit={5} />
      {loading ? (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        backgroundColor: '#fff',
      }}>
        <img src={loadingGif} alt="Loading..." style={{ width: '30%', height: '30%', objectFit: 'contain' }} />
      </div>
    ) : jobFlows && jobFlows.length > 0 ? (
        <>
          <div style={{ flex: '1 0 60%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <TextField
              select
              fullWidth
              label="Select Job Flow"
              value={selectedJobFlow}
              onChange={(e) => setSelectedJobFlow(e.target.value)}
              variant="outlined"
              margin="normal"
            >
              {jobFlows.map((flow) => (
                <MenuItem key={flow.id} value={flow.id}>
                  {flow.flow_name}
                </MenuItem>
              ))}
            </TextField>
            <Paper style={{ flexGrow: 1, marginTop: '0px', marginBottom: '-10px', overflow: 'hidden', boxShadow: 'none' }}>

              <Bar data={chartData} options={chartOptions} />
            </Paper>
          </div>

          <div>
            {productManagementRead === 1 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Typography variant="body2" style={{ color: '#666' }}>
                    Show Price
                  </Typography>
                  <Switch
                    checked={showPrice}
                    onChange={(e) => setShowPrice(e.target.checked)}
                    color="primary"
                  />
                </div>
            )}
          </div>
          
          <div style={{ flex: '1 0 40%', display: 'flex', gap: '10px', marginTop: '0px' }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '0px',
              boxShadow: 'none',
              borderRadius: '10px',
              backgroundColor: '#fff',
              width: '40%',
              overflow: 'hidden',
              maxHeight: '40vh',
              border: '1px solid #e0e0e0',
            }}>
              <h3 style={{
                textAlign: 'left',
                margin: 0,
                fontWeight: 600,
                fontSize: 18,
                letterSpacing: 0.5,
                color: '#222',
                background: 'transparent',
                padding: '16px 16px 8px 16px',
                borderBottom: '1px solid #e5e7eb',
                width: '100%',
                boxSizing: 'border-box',
              }}>
                Recent Orders
              </h3>
              <div style={{
                flex: 1,
                width: '100%',
                overflowY: 'auto',
                padding: '16px 12px 0 12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%',
              }}>
                {recentOrderLoading ? (
                  <img src={loadingGif5} alt="Loading..." style={{ width: '40%', height: '40%', objectFit: 'contain', margin: 'auto', display: 'block' }} />
                ) : recentOrders.length === 0 ? (
                  <div style={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center', marginTop: '16px' }}>No order found.</div>
                ) : (
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px'
                  }}>
                    {recentOrders.map(order => (
                      <li
                        key={order.orderId}
                        style={{
                          width: '100%',
                          minHeight: 48,
                          borderRadius: 10,
                          background: '#f6f8fb',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 16px',
                          transition: 'box-shadow 0.2s, transform 0.2s',
                          cursor: 'pointer',
                          position: 'relative',
                          border: '1px solid #e5e7eb',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                        onClick={() => window.open(`/#/order/${order.orderId}`, '_blank')}
                        onMouseOver={e => {
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(25, 118, 210, 0.12)';
                          e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
                          e.currentTarget.style.transform = 'none';
                        }}
                      >
                        <span style={{ color: '#1976d2', fontWeight: 700 }}>{order.orderName}</span>
                        <span style={{ color: '#3b3b3bff', fontWeight: 500 }}>{order.customerName}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '0px',
              boxShadow: 'none',
              borderRadius: '10px',
              backgroundColor: '#fff',
              width: '20%',
              overflow: 'hidden',
              maxHeight: '40vh',
              border: '1px solid #e0e0e0',
            }}>
              <h3 style={{
                textAlign: 'left',
                margin: 0,
                fontWeight: 600,
                fontSize: 18,
                letterSpacing: 0.5,
                color: '#222',
                background: 'transparent',
                padding: '16px 16px 8px 16px',
                borderBottom: '1px solid #e5e7eb',
                width: '100%',
                boxSizing: 'border-box',
              }}>Overview</h3>
                <div style={{
                  flex: 1,
                  width: '100%',
                  overflowY: 'auto',
                  padding: '0 8px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  height: '100%',
                }}>
                  {overallStatusLoading ? (
                    <img src={loadingGif5} alt="Loading..." style={{ width: '50%', height: '50%', objectFit: 'contain', margin: 'auto', display: 'block' }} />
                  ) : overallStatus ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      alignItems: 'center',
                      margin: '16px 0',
                    }}>
                      {[
                        { label: 'Current Orders', value: overallStatus?.currentOrders ?? 0, color: '#3B82F6' },
                        { label: 'Current Jobs', value: overallStatus?.currentJobs ?? 0, color: '#10B981' },
                        { label: 'Urgent Jobs', value: overallStatus?.urgentJobs ?? 0, color: '#F59E42' },
                      ].map((item, idx) => (
                        <div
                          key={item.label}
                          style={{
                            width: '100%',
                            minHeight: 48,
                            borderRadius: 10,
                            background: '#f6f8fb',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '0 16px',
                            transition: 'box-shadow 0.2s, transform 0.2s',
                            position: 'relative',
                            border: '1px solid #e5e7eb',
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.boxShadow = '2px 2px 8px #e3e6ed, -2px -2px 8px #ffffff';
                            e.currentTarget.style.transform = 'translateY(-1px) scale(1.01)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.boxShadow = '4px 4px 12px #e3e6ed, -4px -4px 12px #ffffff';
                            e.currentTarget.style.transform = 'none';
                          }}
                        >
                          <div style={{
                            width: 8,
                            height: 32,
                            borderRadius: 6,
                            background: item.color,
                            marginRight: 12,
                            flexShrink: 0,
                          }} />
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: '#6b7280',
                              letterSpacing: 0.5,
                            }}>{item.label}</div>
                            <AnimatedNumber value={item.value} color={item.color} small />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography sx={{ color: '#888', fontStyle: 'italic', textAlign: 'center', mt: 2 }}>No data available.</Typography>
                  )}
                </div>
              </div>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginTop: '0px',
              boxShadow: 'none',
              borderRadius: '10px',
              backgroundColor: '#ffffffff',
              width: '40%',
              overflow: 'hidden',
              maxHeight: '40vh',
              border: '1px solid #e0e0e0',
            }}>
              <h3 style={{
                textAlign: 'left',
                margin: 0,
                fontWeight: 600,
                fontSize: 18,
                letterSpacing: 0.5,
                color: '#222',
                background: 'transparent',
                padding: '16px 16px 8px 16px',
                borderBottom: '1px solid #e5e7eb',
                width: '100%',
                boxSizing: 'border-box',
              }}>Urgent Orders</h3>
              <div style={{
                flex: 1,
                width: '100%',
                overflowY: 'auto',
                padding: '16px 12px 0 12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                height: '100%'
              }}>
                {urgentOrderLoading ? (
                  <img src={loadingGif5} alt="Loading..." style={{ width: '50%', height: '50%', objectFit: 'contain', margin: 'auto', display: 'block' }} />
                ) : recentUrgentOrders.length === 0 ? (
                  <div style={{ color: '#888', fontStyle: 'italic', textAlign: 'center' }}>No urgent orders found.</div>
                ) : (
                  <ul style={{
                    listStyle: 'none',
                    padding: 0,
                    margin: 0,
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                  }}>
                    {recentUrgentOrders.map(order => (
                      <li
                        key={order.orderId}

                        style={{
                          width: '100%',
                          minHeight: 48,
                          borderRadius: 10,
                          background: '#f6f8fb',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                          display: 'flex',
                          alignItems: 'center',
                          padding: '0 16px',
                          transition: 'box-shadow 0.2s, transform 0.2s',
                          cursor: 'pointer',
                          position: 'relative',
                          border: '1px solid #e5e7eb',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                        onClick={() => window.open(`/#/order/${order.orderId}`, '_blank')}
                        onMouseOver={e => {
                          e.currentTarget.style.boxShadow = '0 4px 16px rgba(211, 84, 0, 0.12)';
                          e.currentTarget.style.transform = 'translateY(-2px) scale(1.03)';
                        }}
                        onMouseOut={e => {
                          e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)';
                          e.currentTarget.style.transform = 'none';
                        }}
                      >
                        <span style={{ color: '#d35400', fontWeight: 700 }}>{order.orderName}</span>
                        <span style={{ color: '#3b3b3bff', fontWeight: 500 }}>{order.customerName}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>

        </>
      ) : (
        <div className="no-job-flow-message">
          <h2>Welcome to IMS</h2>
          <p>Please create a job flow before proceeding.</p>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
